//@ts-nocheck -> many SDK types are out of date here
import { CONVEYANCE_TYPES, type Location } from '@koala/sdk/v4';
import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { utcToZonedTime, format } from 'date-fns-tz';
import { getFulfillmentTime, isAsapAvailable } from '@/utils/conveyance';
import { useHandoffTimes } from '@/features/handoff/time-picker';
import { useNextAvailableDayWithSlots } from '@/features/handoff/time-picker/use-handoff-times';
import { getAvailableDays } from '@/features/handoff/time-picker/utils';
import GenericModal from '@/components/uielements/genericModal';
import {
  StyledGenericModalContent,
  StyledGenericModalFooter,
} from '@/components/uielements/genericModal/styles';
import { getStoreTimeInfo } from '@/utils/locations';
import { MODAL } from '@/constants/events';
import { type IBasketState } from '@/types/basket';
import { type IConveyanceModeState } from '@/types/conveyanceMode';
import { StyledPrimaryButton } from '@/components/uielements/primaryButton/styles';
import conveyanceModeActions from '@/redux/conveyanceMode/actions';
import { isUrlServiceTypeDelivery } from '@/utils/urls';

interface Props {
  basket: IBasketState;
  address: IConveyanceModeState['address'];
  location?: Location;
  timeWanted: string;
  conveyanceType: CONVEYANCE_TYPES;
}

/* Modal popup if outside of business/operating hours */
export const ConveyanceReconciliation = ({
  basket,
  address,
  location,
  timeWanted,
  conveyanceType,
}: Props) => {
  const { isOpen, hasRemainingHoursToday, storeDate, nextAvailableDateInStore, storeTimezone } =
    getStoreTimeInfo(location, conveyanceType);
  const wanted_at = basket?.checkoutBasket?.wanted_at ?? timeWanted;
  const [mounted, setMounted] = useState(false);
  const [modalShown, setModalShown] = useState(false);
  const dispatch = useDispatch();
  const availableDays = getAvailableDays(location, conveyanceType);
  const firstLegalDay = availableDays.find((d) => !d.disabled)?.value;

  let wantedAt = wanted_at;
  if (isAsapAvailable(location, conveyanceType) && !wanted_at) {
    wantedAt = '';
  } else {
    wantedAt = wanted_at
      ? format(utcToZonedTime(wanted_at, storeTimezone), 'yyyy-MM-dd')
      : nextAvailableDateInStore;
    // Use the picker's first legal day instead of blindly using today — on advance-notice stores,
    // today is before the earliest orderable date and seeding it leads to a rejected order
    // (TEAM-14277). For stores with no advance notice, firstLegalDay is today anyway.
    wantedAt = !hasRemainingHoursToday ? wantedAt : firstLegalDay ?? storeDate;
  }

  const getHandoffType = () => {
    if (!location) {
      return CONVEYANCE_TYPES.PICKUP;
    }
    return location?.supports_pick_up ? CONVEYANCE_TYPES.PICKUP : CONVEYANCE_TYPES.DELIVERY;
  };

  const { data: handoffTimes, isSuccess } = useHandoffTimes({
    dayWanted: wantedAt,
    handoffType: getHandoffType(),
    location: location,
  });

  // When ASAP is structurally unavailable and the
  // customer arrives with no chosen time, seed the earliest real slot so
  // the menu, cart, and time chip reflect an orderable time. Guarded by a ref so
  // it never overwrites an explicit customer selection on later re-renders.
  const { data: nextAvailableWithSlots } = useNextAvailableDayWithSlots({
    availableDays,
    handoffType: conveyanceType,
    location,
  });
  const autoSetOnMountRef = useRef(false);
  useEffect(() => {
    if (autoSetOnMountRef.current) return;
    if (!location) return;
    if (isAsapAvailable(location, conveyanceType)) return;
    if (timeWanted || basket?.checkoutBasket?.wanted_at) return;
    if (!nextAvailableWithSlots?.firstAvailableTime) return;

    autoSetOnMountRef.current = true;
    dispatch(
      conveyanceModeActions.setWantedTime(
        nextAvailableWithSlots.firstAvailableTime,
        location.id,
        conveyanceType,
      ),
    );
  }, [
    location,
    conveyanceType,
    timeWanted,
    basket?.checkoutBasket?.wanted_at,
    nextAvailableWithSlots?.firstAvailableTime,
    dispatch,
  ]);

  useEffect(() => {
    if (!isUrlServiceTypeDelivery() && !mounted) {
      // Don't surface the reconciliation modal if the user has already chosen
      // a wanted_at time on checkout — auto-overriding would silently revert it.
      setModalShown(!isOpen && !basket?.checkoutBasket?.wanted_at);
      setMounted(true);
    }
  }, [isOpen, mounted, handoffTimes, basket?.checkoutBasket?.wanted_at]);

  const startOrder = () => {
    dispatch(conveyanceModeActions.setWantedTime(handoffTimes[0], location.id, conveyanceType));
    setModalShown(false);
  };

  // Set the wanted at time to fetch the menu for the given time.
  // Skip if Path A (advance-notice-aware seeder) already committed a value — its ref guard
  // means it picked the first legal slot, and this effect must not overwrite it with today's
  // slots (TEAM-14277).
  useEffect(() => {
    if (autoSetOnMountRef.current) return;
    if (modalShown && handoffTimes && handoffTimes.length > 0) {
      dispatch(conveyanceModeActions.setWantedTime(handoffTimes[0], location.id, conveyanceType));
    }
  }, [modalShown, handoffTimes, dispatch, location, conveyanceType]);

  if (!(isSuccess && handoffTimes && handoffTimes.length > 0)) {
    return null;
  }

  return (
    <div>
      <GenericModal
        modalOpen={modalShown && location?.supports_pick_up}
        name={MODAL.OUTSIDE_OPERARING_HOURS}
        header={''}
      >
        <StyledGenericModalContent>
          <div style={{ padding: '40px 20px 20px 20px' }}>
            Your earliest available{' '}
            {basket?.fulfillment?.type || address?.type || CONVEYANCE_TYPES.PICKUP} time is:
            <br></br>
            {getFulfillmentTime(handoffTimes[0] ?? wantedAt, location)}
          </div>
        </StyledGenericModalContent>
        <StyledGenericModalFooter style={{ display: 'flex', justifyContent: 'center' }}>
          <StyledPrimaryButton
            data-css-override="StyledPrimaryButton"
            aria-label="Start order"
            onClick={startOrder}
          >
            Start Order
          </StyledPrimaryButton>
        </StyledGenericModalFooter>
      </GenericModal>
    </div>
  );
};
